/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  AuthConfigurationV3,
  DomainToken,
  ProvisionDevicesResponse,
  ProvisionDevicesResponseV2
} from "@tivocorp/data-domain-lib";

/** Identifies which screen element currently holds D-pad focus. */
export type SignInFocusedField =
  | "tab"
  | "userId"
  | "password"
  | "passwordToggle"
  | "signIn"
  | "needHelp"
  | "getNewCode";

/** DBS polling status. */
export type DbsPollingStatus =
  | "idle"
  | "polling"
  | "expired"
  | "success"
  | "error";

/** DBS flow overall status. */
export type DbsFlowStatus =
  | "idle"
  | "claiming"
  | "fetching-dbs-token"
  | "generating-license-plate"
  | "awaiting-user-bind"
  | "binding"
  | "fetching-domain-token"
  | "provisioning"
  | "completed"
  | "error";

/**
 * Typed DBS error codes for deterministic client behavior.
 */
export type DbsErrorCode =
  | "DBS.BAD_REQUEST"
  | "DBS.RESOURCE_NOT_FOUND"
  | "DBS.LICENSE_PLATE_INVALID"
  | "DBS.INTERNAL_SERVER_ERROR"
  | "DBS.INTEGRATION_TIMEOUT"
  | "APIGW_ERROR"
  | "BIND_FAILED"
  | "PROVISION_FAILED"
  | "UNKNOWN";

/**
 * Typed DBS error for the ViewModel.
 */
export interface DbsError {
  code: DbsErrorCode;
  message: string;
  retryable: boolean;
}

export interface ResetIdentityPayload {
  partnerCustomerId: string;
  identityId: string;
  msoName: string;
  email: string;
}

// --- IP Auto-Login types ---

/** Silent orchestration status for the displayRank walk. */
export type AuthOrchestrationStatus =
  | "idle"
  | "resolving"
  | "ip-auto-login"
  | "interactive"
  | "completed"
  | "failed";

export const AUTH_ORCHESTRATION_IDLE: AuthOrchestrationStatus = "idle";
export const AUTH_ORCHESTRATION_RESOLVING: AuthOrchestrationStatus =
  "resolving";
export const AUTH_ORCHESTRATION_IP_AUTO_LOGIN: AuthOrchestrationStatus =
  "ip-auto-login";
export const AUTH_ORCHESTRATION_INTERACTIVE: AuthOrchestrationStatus =
  "interactive";
export const AUTH_ORCHESTRATION_COMPLETED: AuthOrchestrationStatus =
  "completed";
export const AUTH_ORCHESTRATION_FAILED: AuthOrchestrationStatus = "failed";

/**
 * How the orchestrator treats an authenticationType during the
 * displayRank walk.
 */
export type AuthTypeKind = "silent" | "interactive" | "unsupported";

export const AUTH_TYPE_KIND_SILENT: AuthTypeKind = "silent";
export const AUTH_TYPE_KIND_INTERACTIVE: AuthTypeKind = "interactive";
export const AUTH_TYPE_KIND_UNSUPPORTED: AuthTypeKind = "unsupported";

/**
 * IP auto-login step status. Doubles as the step identifier passed to the
 * error classifier, so a step and the status it reports can never drift.
 */
export type IpAutoLoginStatus =
  | "idle"
  | "claiming"
  | "fetching-ip-jwt"
  | "binding"
  | "fetching-domain-token"
  | "provisioning"
  | "completed"
  | "error";

export const IP_AUTO_LOGIN_STATUS_IDLE: IpAutoLoginStatus = "idle";
export const IP_AUTO_LOGIN_STATUS_CLAIMING: IpAutoLoginStatus = "claiming";
export const IP_AUTO_LOGIN_STATUS_FETCHING_IP_JWT: IpAutoLoginStatus =
  "fetching-ip-jwt";
export const IP_AUTO_LOGIN_STATUS_BINDING: IpAutoLoginStatus = "binding";
export const IP_AUTO_LOGIN_STATUS_FETCHING_DOMAIN_TOKEN: IpAutoLoginStatus =
  "fetching-domain-token";
export const IP_AUTO_LOGIN_STATUS_PROVISIONING: IpAutoLoginStatus =
  "provisioning";
export const IP_AUTO_LOGIN_STATUS_COMPLETED: IpAutoLoginStatus = "completed";
export const IP_AUTO_LOGIN_STATUS_ERROR: IpAutoLoginStatus = "error";

/** IP auto-login error codes for classification. */
export type IpAutoLoginErrorCode =
  | "IPL.BAD_REQUEST"
  | "IPL.NOT_ELIGIBLE"
  | "IPL.ACCOUNT_NOT_FOUND"
  | "IPL.CONFLICT"
  | "IPL.INTERNAL_SERVER_ERROR"
  | "IPL.INTEGRATION_TIMEOUT"
  | "IPL.NETWORK_ERROR"
  | "IPL.TIMEOUT"
  | "IPL.EMPTY_CREDENTIAL"
  | "IPL.NO_ENDPOINT"
  | "IPL.CANCELLED"
  | "IPL.DEVICE_IDENTITY_RESET"
  | "BIND_FAILED"
  | "DOMAIN_TOKEN_FAILED"
  | "PROVISION_FAILED"
  | "UNKNOWN";

export const IPL_BAD_REQUEST: IpAutoLoginErrorCode = "IPL.BAD_REQUEST";
export const IPL_NOT_ELIGIBLE: IpAutoLoginErrorCode = "IPL.NOT_ELIGIBLE";
export const IPL_ACCOUNT_NOT_FOUND: IpAutoLoginErrorCode =
  "IPL.ACCOUNT_NOT_FOUND";
export const IPL_CONFLICT: IpAutoLoginErrorCode = "IPL.CONFLICT";
export const IPL_INTERNAL_SERVER_ERROR: IpAutoLoginErrorCode =
  "IPL.INTERNAL_SERVER_ERROR";
export const IPL_INTEGRATION_TIMEOUT: IpAutoLoginErrorCode =
  "IPL.INTEGRATION_TIMEOUT";
export const IPL_NETWORK_ERROR: IpAutoLoginErrorCode = "IPL.NETWORK_ERROR";
export const IPL_TIMEOUT: IpAutoLoginErrorCode = "IPL.TIMEOUT";
export const IPL_EMPTY_CREDENTIAL: IpAutoLoginErrorCode =
  "IPL.EMPTY_CREDENTIAL";
export const IPL_NO_ENDPOINT: IpAutoLoginErrorCode = "IPL.NO_ENDPOINT";
export const IPL_CANCELLED: IpAutoLoginErrorCode = "IPL.CANCELLED";
/**
 * Provisioning answered 410: this device identity was removed server-side. A
 * new one has been generated, and IP auto-login should be tried again with it
 * rather than marked attempted — the walk re-runs it once.
 */
export const IPL_DEVICE_IDENTITY_RESET: IpAutoLoginErrorCode =
  "IPL.DEVICE_IDENTITY_RESET";

/**
 * Codes shared by DbsErrorCode and IpAutoLoginErrorCode — annotated with
 * their literal type so they stay assignable to both unions.
 */
export const ERROR_CODE_BIND_FAILED = "BIND_FAILED" as const;
export const ERROR_CODE_DOMAIN_TOKEN_FAILED = "DOMAIN_TOKEN_FAILED" as const;
export const ERROR_CODE_PROVISION_FAILED = "PROVISION_FAILED" as const;
export const ERROR_CODE_UNKNOWN = "UNKNOWN" as const;

/**
 * A terminal sign-in failure on its way to the user.
 *
 * Thunks reject with this rather than a bare message so the catalog code
 * survives the trip to the view. Before UAW-2416 every thunk ended in
 * `rejectWithValue(error.message)`, which discarded `code` one line after the
 * error class had computed it — leaving the alert with no support handle to
 * show and nothing to select a recovery action from.
 */
export interface SignInErrorPayload {
  /** Catalog error code (`A`-prefixed), or the `A010` catch-all. */
  code: string;
  /** Localised, user-facing body text. */
  message: string;
  /**
   * The service's own code from the response body (`IAS.UNAUTHORIZED`,
   * `unauthorized`, …), empty when the failure carried no envelope.
   *
   * Shown to the user in preference to {@link SignInErrorPayload.code}: it
   * names the actual cause, where the catalog code for an endpoint with no
   * allocated family — `v1/idpJwtToken`, where a rejected credential lands —
   * is only ever the `A010` catch-all. The catalog code still goes to
   * telemetry and still selects the recovery actions.
   */
  serviceCode: string;
}

/**
 * Typed IP auto-login error for the ViewModel.
 * Uses fallbackEligible (not retryable) — different semantics from DbsError.
 */
export interface IpAutoLoginError {
  /**
   * Catalog error code (`A`-prefixed) — the value that reaches the spec's
   * `signIn` analytics event and the user-facing alert.
   *
   * Separate from {@link IpAutoLoginError.reason} because the two answer
   * different questions. Conflating them was UAW-2416's B2 defect: the
   * classifier's own vocabulary was reaching telemetry as `error_code`, where
   * the pipeline could derive no `error_class` from it.
   *
   * Steps that have a real catalog family (`/domainToken`,
   * `/provisionDevices`) carry their precise code here. Steps whose family is
   * still blocked upstream (`/bind`, `/v1/ip-autologin/token`) carry the
   * `A010` catch-all rather than a colliding number.
   */
  code: string;
  /**
   * The classifier's finer-grained cause. Drives orchestration and stays in
   * diagnostics, so no detail is lost while the catalog families are pending.
   */
  reason: IpAutoLoginErrorCode;
  message: string;
  /** True when the orchestrator should advance to the next displayRank entry. */
  fallbackEligible: boolean;
}

/**
 * Message thrown by the interactor when the IP autologin response has an empty JWT.
 * Used for error classification in the thunk — match on this constant, not a duplicated literal.
 */
export const IP_AUTOLOGIN_EMPTY_CREDENTIAL_MESSAGE: string =
  "IP autologin JWT token is empty in service response";

/**
 * Persisted authentication context for token refresh routing.
 */
export interface TLastAuthContext {
  authenticationType: string;
  authorizationSchemes: {
    claim: string;
    bind: string;
    domainToken: string;
  };
}

/**
 * State interface for the Sign In feature slice.
 */
export interface SignInState {
  /** Whether app startup initialization (endpoints + auth config) is in progress. */
  isInitializing: boolean;
  /** Whether app startup initialization has completed successfully. */
  isInitialized: boolean;
  /** Error message if app startup initialization failed. */
  initError: string;
  /**
   * Catalog code for {@link SignInState.initError} — the A1xx auth-config
   * family, or the `A010` catch-all — empty when there is no error.
   */
  initErrorCode: string;
  /** The service's own code for {@link SignInState.initError}, or empty. */
  initErrorServiceCode: string;
  /** Auth configs fetched from v3/authconfigurations; drives tab rendering. */
  authConfigurations: AuthConfigurationV3[];
  /** Currently active sign-in tab (authenticationType of selected config). */
  activeTab: string;
  /** User ID input value. */
  userId: string;
  /** Password input value. */
  password: string;
  /** Whether the password field content is visible. */
  isPasswordVisible: boolean;
  /** The authorization code displayed on the Use Code screen. */
  authCode: string;
  /** License plate URL used to generate the QR code. */
  qrCodeUrl: string;
  /** Base64-encoded QR code bitmap generated from qrCodeUrl. */
  qrCodeBitmap: string;
  /** Which element currently holds D-pad focus. */
  focusedField: SignInFocusedField;
  /** Whether credential sign-in is in progress (Steps 3–5). */
  isSigningIn: boolean;
  /** Error message if credential sign-in failed. */
  signInError: string;
  /**
   * Catalog code for {@link SignInState.signInError}, empty when there is no
   * error. Kept beside the message rather than folded into it because the two
   * have different jobs: the message is copy, the code is a routing key — it
   * selects the recovery actions and is the string a user reads to support.
   */
  signInErrorCode: string;
  /**
   * The service's own code for {@link SignInState.signInError} — what the
   * alert renders in parentheses. Empty when the failure carried no response
   * envelope, in which case the alert falls back to
   * {@link SignInState.signInErrorCode}.
   */
  signInErrorServiceCode: string;
  /** Domain token received after successful credential sign-in (Step 4). */
  domainToken: DomainToken | null;
  /** Provision devices response received after successful device provisioning (Step 5). */
  provisionResponse:
    | ProvisionDevicesResponse
    | ProvisionDevicesResponseV2
    | undefined;
  /** Whether the async IndexedDB token rehydration is in progress. */
  isTokenRehydrating: boolean;
  /** Whether a silent domain-token refresh is currently in progress. */
  isRefreshingToken: boolean;
  /** Error message from the most recent silent token refresh attempt. */
  refreshError: string;

  /** Recovery email input for forgot-password browser flow. */
  recoveryEmail?: string;
  /** Whether forgot-password request is in flight. */
  isSendingResetLink?: boolean;
  /** Last forgot-password request error. */
  sendResetLinkError?: string;
  /** Whether reset token validation request is in flight. */
  isResetTokenValidating?: boolean;
  /** Last reset token validation error. */
  resetTokenValidationError?: string;
  /** Result of latest reset token validation. */
  resetIdentityPayload?: ResetIdentityPayload | null;
  /** Whether update password request is in flight. */
  isUpdatingPassword?: boolean;
  /** Last update password request error. */
  updatePasswordError?: string;
  /** Indicates update password success for browser flow. */
  isPasswordResetComplete?: boolean;

  // --- DBS License Plate Binding state ---

  /** Overall DBS flow status. */
  dbsFlowStatus: DbsFlowStatus;
  /** DBS-specific scoped token from DTS (used to call DBS APIs). */
  dbsToken: string;
  /** Expiration epoch (seconds) of the DBS-specific token. */
  dbsTokenExpiration: number;
  /** License plate code displayed to the user. */
  licensePlateCode: string;
  /** Opaque credential returned alongside the LP code. */
  licensePlateCredential: string;
  /** Polling interval in seconds returned by DBS. */
  pollingIntervalSeconds: number;
  /** UTC expiration time string for the license plate. */
  licensePlateExpirationTime: string;
  /** URL where the user enters the LP code (from dbsMetaData). */
  licensePlateBindingUrl: string;
  /** Credential URL template for QR code (from dbsMetaData). */
  licensePlateBindingCredentialUrl: string;
  /** DBS JWT token returned after user completes LP binding on MSO site. */
  dbsJwtToken: string;
  /** Polling status for dbsJwtToken calls. */
  dbsPollingStatus: DbsPollingStatus;
  /** Typed DBS error for UI display and retry logic. */
  dbsError: DbsError | null;
  /** Whether the mobile landing dialog has been dismissed on the current route visit. */
  mobileDialogDismissed?: boolean;

  // --- IP Auto-Login orchestration state ---

  /** Silent orchestration status for the displayRank walk. */
  authOrchestrationStatus: AuthOrchestrationStatus;
  /** Auth types already attempted in this session; prevents re-entry. */
  attemptedAuthTypes: string[];
  /** Sub-step progress for the IP auto-login attempt. */
  ipAutoLoginStatus: IpAutoLoginStatus;
  /** Last IP auto-login failure. */
  ipAutoLoginError: IpAutoLoginError | null;
  /** The authenticationType that produced the current domainToken; drives refresh. */
  resolvedAuthType: string;
}
